// Theme v2 CSS (presentation-2 contract section 4 and face templates section 2.6): the v1 color
// tokens plus materials, fonts, elevation, motion tokens and text roles, as custom properties
// scoped to the scene root.

import { themeCss } from '../presentation/theme.ts';
import type { FontName, TextRole, TextRoleStyle, ThemeV2 } from '../presentation/types.ts';
import { motionTokens } from '../motion/planner.ts';
import { materialBackground, materialInk, materialTint, shade } from './materials.ts';

const STACKS: Record<FontName, string> = {
  Fraunces: '"Fraunces", Georgia, Cambria, serif',
  Cinzel: '"Cinzel", "Trajan Pro", Georgia, serif',
  'Alegreya Sans': '"Alegreya Sans", system-ui, "Segoe UI", sans-serif',
  Nunito: '"Nunito", ui-rounded, system-ui, sans-serif',
  'IBM Plex Mono': '"IBM Plex Mono", ui-monospace, Consolas, monospace',
};

export const DEFAULT_ROLES: Record<TextRole, Required<Omit<TextRoleStyle, 'italic' | 'caps'>> & { italic: boolean; caps: boolean }> = {
  title: { font: 'display', weight: 700, size: 6, italic: false, caps: false },
  subtitle: { font: 'body', weight: 500, size: 4.2, italic: false, caps: false },
  rules: { font: 'body', weight: 400, size: 3.6, italic: false, caps: false },
  flavor: { font: 'body', weight: 400, size: 3.2, italic: true, caps: false },
  number: { font: 'mono', weight: 600, size: 6, italic: false, caps: false },
  caption: { font: 'body', weight: 500, size: 4, italic: false, caps: false },
};

export function fontStack(name: FontName | undefined, fallback: string): string {
  return name ? STACKS[name] : fallback;
}

/** Resolves a text role to a CSS font family, weight, size (face units) and style. */
export function textRole(theme: ThemeV2, role: string): { family: string; weight: number; size: number; italic: boolean; caps: boolean } {
  const base = DEFAULT_ROLES[(role in DEFAULT_ROLES ? role : 'caption') as TextRole];
  const style = { ...base, ...(theme.textRoles?.[role as TextRole] ?? {}) };
  const family = style.font === 'display' ? 'var(--font-display)' : style.font === 'body' ? 'var(--font-body)' : style.font === 'mono' ? 'var(--font-mono)' : STACKS[style.font as FontName] ?? 'var(--font-body)';
  return { family, weight: style.weight, size: style.size, italic: !!style.italic, caps: !!style.caps };
}

const ELEVATION = {
  flat: { lift: '0 1px 2px rgb(0 0 0 / 18%)', raised: '0 1px 3px rgb(0 0 0 / 22%)' },
  soft: { lift: '0 6px 16px rgb(0 0 0 / 28%), 0 2px 4px rgb(0 0 0 / 20%)', raised: '0 2px 6px rgb(0 0 0 / 25%)' },
  deep: { lift: '0 14px 30px rgb(0 0 0 / 40%), 0 4px 8px rgb(0 0 0 / 30%)', raised: '0 4px 12px rgb(0 0 0 / 35%)' },
};

/** CSS for a presentation-2 theme. Seat colors and v1 tokens come from the v1 theme CSS. */
export function themeCssV2(theme: ThemeV2, seats: string[]): string {
  const table = theme.material?.table ?? 'felt';
  const mats = theme.material?.mats ?? 'wood';
  const tableTint = materialTint(table, theme.material?.tint);
  const matTint = materialTint(mats, theme.material?.matTint);
  const ink = materialInk(table, theme.material?.tint), matInk = materialInk(mats, theme.material?.matTint);
  const elevation = ELEVATION[theme.elevation ?? 'soft'];
  const motion = motionTokens(theme.motion?.speed ?? 'brisk');
  const vars = [
    `--table-bg: ${materialBackground(table, theme.material?.tint)};`,
    `--table-tint: ${tableTint};`, `--table-ink: ${ink.ink};`, `--table-ink-muted: ${ink.muted};`,
    `--table-panel: color-mix(in srgb, ${shade(tableTint, -0.55)} 78%, transparent);`,
    `--mat-bg: ${materialBackground(mats, theme.material?.matTint)};`,
    `--mat-tint: ${matTint};`, `--mat-ink: ${matInk.ink};`, `--mat-ink-muted: ${matInk.muted};`,
    `--font-display: ${fontStack(theme.fonts?.display, 'var(--font-sans)')};`,
    `--font-body: ${fontStack(theme.fonts?.body, 'var(--font-sans)')};`,
    `--font-mono: ${STACKS['IBM Plex Mono']};`,
    `--shadow-lift: ${elevation.lift};`, `--shadow-raised: ${elevation.raised};`,
    `--motion-move: ${motion.move}ms;`, `--motion-flip: ${motion.flip}ms;`, `--motion-pulse: ${motion.pulse}ms;`, `--motion-stagger: ${motion.stagger}ms;`,
    `--motion-easing: ${motion.easing};`,
  ];
  return `${themeCss(theme, seats)} .scene-root { ${vars.join(' ')} }`;
}
